/* eslint-disable complexity */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { CompIcon } from 'common/react/components/CompIcon';
import { TeamIcon } from 'common/react/components/TeamIcon';
import { getTranslation } from 'common/react/utils/translations';

const DROPDOWN_VERTICAL_OFFSET = 70;

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {Function} [props.selectedItemToString] - The selected item's text displayed on the input
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {boolean} [props.isDisabled] - Is select disabled.
 * @param {boolean} [props.showCompLogoInside] - Displays comp logo alongside option
 * @param {object} [props.defaultItem] - Specify object to display as default item
 * @param {Function} [props.onReset] - Reset button handler
 * @returns {React.Element} Component.
 */
export const SelectDesktopWithDefault = (props) => {
    const selectRef = useRef();
    const toggleButtonRef = useRef();

    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        display: 'none'
    });

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        closeMenu,
        highlightedIndex,
        getItemProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );

            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        },
        stateReducer
    });

    const currentRound =
        typeof props?.currentRoundIndex !== 'undefined'
            ? props.currentRoundIndex
            : false;

    const isDisabled = props?.isDisabled;

    // remove our default item from the list
    const defaultIndex = props.items.indexOf(props.defaultItem);
    if (defaultIndex > -1) {
        props.items.splice(defaultIndex, 1);
    }

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    const getSelectedItemText = (item) => {
        switch (true) {
            case !!props.selectedItemToString:
                return props.selectedItemToString(item);
            case !!props.itemToString:
                return props.itemToString(item);
            default:
                return item.name;
        }
    };

    /**
     * @param {object} state - Current state.
     * @param {object} actionAndChanges - Action and changes object.
     * @returns {object} The new state or changes object.
     */
    function stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges;
        // this prevents the menu from being closed when the user selects an item with 'Enter' or mouse
        switch (type) {
            case useSelect.stateChangeTypes.ToggleButtonBlur:
                return state;
            default:
                return changes; // otherwise business as usual.
        }
    }

    const onReset = () => {
        toggleButtonRef?.current.focus();
        props.onReset();
    };

    const onElementBlur = (evt) => {
        const element = evt?.relatedTarget || evt?.target;
        if (
            !selectRef.current?.contains(element) &&
            !element.innerHTML.includes(getTranslation('label.clear'))
        ) {
            closeMenu();
        }
    };

    const onClickOutside = (evnt) => {
        if (!selectRef.current?.contains(evnt.target)) {
            closeMenu();
        }
    };

    useEffect(() => {
        document.addEventListener('click', onClickOutside);

        return () => {
            document.removeEventListener('click', onClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        if (isOpen && toggleButtonRef.current) {
            const rect = toggleButtonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY - DROPDOWN_VERTICAL_OFFSET,
                left: rect.left + window.scrollX,
                width: rect.width,
                display: 'block'
            });
        } else {
            setDropdownPosition((pos) => ({ ...pos, display: 'none' }));
        }
    }, [isOpen]);

    return (
        <div
            className={classNames('select', {
                'select--open': isOpen
            })}
            ref={selectRef}
        >
            <button
                type="button"
                {...getToggleButtonProps({
                    id: `${PULSE.app.common.idifyString(
                        props.label
                    )}-dropdown-button`,
                    'aria-labelledby': !props.hideLabel
                        ? `${PULSE.app.common.idifyString(
                              props.label
                          )}-dropdown-label`
                        : false,
                    'aria-label': props.hideLabel ? props.label : false,
                    ref: toggleButtonRef
                })}
                className="select__display"
                disabled={isDisabled}
                onBlur={onElementBlur}
            >
                {selectedItem?.default && props.icon ? ( // display default icon if default selected
                    <span className={`competition-nav__icon`}>
                        <SvgIcon
                            icon={props.icon}
                            className="icon icon--placeholder"
                        />
                    </span>
                ) : props.showTeamLogo && selectedItem.teamAbbr ? (
                    <TeamIcon
                        className="select__option-logo-icon"
                        providerId={selectedItem.providerId}
                    />
                ) : props.showCompLogo && selectedItem?.code ? (
                    <span
                        className={`competition-nav__comp-icon competition-nav__comp-icon--${selectedItem.code.toLowerCase()}`}
                    >
                        <CompIcon code={selectedItem.code} className="icon" />
                    </span>
                ) : props.icon ? (
                    <span className={`competition-nav__icon`}>
                        <SvgIcon
                            icon={props.icon}
                            className="icon icon--placeholder"
                        />
                    </span>
                ) : null}
                <span className="select__content">
                    {props.label && !props.hideLabel ? (
                        <label
                            {...getLabelProps({
                                id: `${PULSE.app.common.idifyString(
                                    props.label
                                )}-dropdown-label`
                            })}
                            className="select__label"
                        >
                            {props.label}
                        </label>
                    ) : null}
                    <span className="select__current">
                        <span className="select__current-text">
                            {getSelectedItemText(selectedItem) || ''}
                        </span>
                        <SvgIcon
                            icon="chevron-down"
                            className={classNames('select__chevron', {
                                'select__chevron--open': isOpen
                            })}
                        />
                    </span>
                </span>
            </button>
            {ReactDOM.createPortal(
                <div
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        zIndex: 80,
                        display: dropdownPosition.display
                    }}
                >
                    <div className="select__options-wrapper">
                        {isOpen &&
                        selectedItem &&
                        props.onReset &&
                        props.defaultItem ? (
                            <div className="select__header">
                                {props.title}
                                {props.defaultItem.id !== selectedItem.id ? (
                                    <button
                                        type="button"
                                        className="select__header-clear button-new button-new--secondary"
                                        tabIndex={0}
                                        onClick={onReset}
                                        onBlur={onElementBlur}
                                    >
                                        {getTranslation('label.clear')}
                                    </button>
                                ) : null}
                            </div>
                        ) : null}
                        <ul
                            {...getMenuProps(
                                {
                                    'aria-labelledby': `${PULSE.app.common.idifyString(
                                        props.label
                                    )}-dropdown-label`
                                },
                                { suppressRefError: true }
                            )}
                            className={classNames('select__options-list', {
                                'u-hide': !isOpen
                            })}
                            onBlur={onElementBlur}
                        >
                            {isOpen &&
                                props.items?.map((item, index) => {
                                    const itemText = getItemText(item);
                                    const isSelected =
                                        getItemText(selectedItem) === itemText;

                                    return (
                                        <li
                                            key={`${itemText}${index}`}
                                            {...getItemProps({ item, index })}
                                            className={classNames(
                                                'select__option',
                                                {
                                                    'select__option--selected':
                                                        isSelected,
                                                    'select__option--current':
                                                        currentRound === index,
                                                    'select__option--highlighted':
                                                        highlightedIndex ===
                                                        index
                                                }
                                            )}
                                        >
                                            <div className="select__option-logo-name-wrapper">
                                                {props.showTeamLogo &&
                                                    item.providerId && (
                                                        <TeamIcon
                                                            className="select__option-logo-icon"
                                                            providerId={
                                                                item.providerId
                                                            }
                                                        />
                                                    )}
                                                {props.showCompLogo &&
                                                    props.showCompLogoInside &&
                                                    selectedItem && (
                                                        <span
                                                            className={`select__option-logo-icon--comp competition-nav__comp-icon competition-nav__comp-icon--internal competition-nav__comp-icon--${item?.code?.toLowerCase()}`}
                                                        >
                                                            <CompIcon
                                                                code={
                                                                    item?.code
                                                                }
                                                                className="icon"
                                                            />
                                                        </span>
                                                    )}
                                                <span className="item-text">
                                                    {itemText}
                                                </span>
                                            </div>

                                            {isSelected && (
                                                <SvgIcon
                                                    icon={`checkmark`}
                                                    className="select__option-button-icon"
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

SelectDesktopWithDefault.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    selectedItemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hideLabel: PropTypes.bool,
    showTeamLogo: PropTypes.bool,
    isOpen: PropTypes.bool,
    icon: PropTypes.string,
    title: PropTypes.string,
    showCompLogoInside: PropTypes.bool,
    defaultItem: PropTypes.object,
    onReset: PropTypes.func
};
